/* eslint no-unused-vars:0 */

import router from "@/router";
import axios from "axios";
import {incomePlanAxios as altAxios} from '../../axios';
import ClientPortalSetting from "../models/ClientPortalSetting";
const NAVIGATION_ORDER_SET = 'NAVIGATION_ORDER_SET';
const NAVIGATION_ORDER_DEFAULT = '[1, 2, 3, 4, 5, 6]';
import {resetRequestingUrl} from "@/portal-setting-signature";

export const auth = {
    namespaced: true,
    state: {
        user: null,
        adminMode: false,
        loggingOut: false,
        clientPortalSetting: new ClientPortalSetting(),
        clientPortalSettingSignature: null,
        portfolios: [],
        portfolioCategories: [],
        portfolioId: null, // Last chosen portfolio
    },

    actions: {
        login ({ commit }, credentials) {
            return axios.get('/client/csrf-cookie').then(() => {
                return axios
                    .post('/client/login', credentials)
                    .then(response => {
                        if (response.data.status !== 'pending_verification') {
                            commit('setUserData', response.data.data.user);
                        }
                        return response;
                    })
            });
        },

        logout ({ commit }) {
            commit('setLoggingOut', true);
            return axios.get('/client/csrf-cookie').then(() => {
                return axios
                    .post('/client/logout')
                    .then(response => {
                        commit('clearUserData');
                        return response;
                    });
            });
        },

        requestLoginVerification({commit}, phone)
        {
            return axios.post('/client/login-verification', {phone})
        },

        verifyLogin ({ commit }, {code, email, password}) {
            return axios
                .post('/client/verify-login', {
                    email,
                    password,
                    login_verification_code: code
                })
                .then(
                    response => {
                        if (response.data.status !== 'success') {
                            return Promise.reject(response) ;
                        }
                        commit('setUserData', response.data.data.user);
                        return response;
                    }
                );
        },


        logoutAdmin ({ commit }) {
            commit('clearUserData');
            commit('setAdminMode', false)
            localStorage.removeItem('vuex');
        },

        register ({ commit }, formData) {
            return axios.get('/client/csrf-cookie').then(() => {
                return axios
                    .post('/client/register', formData)
                    .then(response => {
                        commit('setUserData', response.data.data.user);
                        return response;
                    })
            });
        },

        resetPassword ({ commit }, formData) {
            return axios.get('/client/csrf-cookie').then(() => {
                return axios
                    .post('/client/password/email', formData)
                    .then(response => {
                        return response;
                    })
            });
        },

        browseAsAdmin({ commit }) {
            return axios.get('/client/csrf-cookie')
                .then(() => {
                    return axios.get('/client/isAdmin');
                })
                .then((response) => {
                    let data = response.data.data;
                    commit('setAdminMode', data.isAdmin);
                    commit('setUserData', data.userData);
                    commit('settings/setCompanyKey', data.portalLoginKey, {root: true});
                    return data.isAdmin;
                });
        },

        /**
         * Set the user preferred navigation order
         * @param commit
         * @param state
         * @param payload
         * @returns {Promise<void>}
         */
        setNavigationOrder ({commit, state}, {payload}) {
            return altAxios.put(
                process.env.VUE_APP_BASE_URL + '/api/users/'+ state.user.id,
                payload
            ).then(
                response => response.data.data
            ).then(
                () => {
                    commit(NAVIGATION_ORDER_SET, payload);
                }
            );
        },

        /**
         * Set the user preferred navigation order
         * @param commit
         * @param state
         * @param payload
         * @returns {Promise<void>}
         */
        updateUserData ({commit, state}, payload) {
            return altAxios.put(
                process.env.VUE_APP_BASE_URL + '/api/users/'+ state.user.id,
                payload
            ).then(
                response => response.data.data
            ).then(
                ({user}) => {
                    commit("setUserData", user);
                }
            );
        },
    },
    mutations: {
        setUserData (state, userData) {
            state.user = userData;
            if (state.user.portal_navigation_order == null) {
                state.user.portal_navigation_order = NAVIGATION_ORDER_DEFAULT;
            }

            if (userData.portfolioCategories) {
                state.portfolioCategories = [...userData.portfolioCategories];
            }
        },
        setPortfolios(state, portfolios) {
            state.portfolios = [...portfolios];
        },
        clearUserData(state) {
            state.loggingOut = false;
            state.user = null;
            state.clientPortalSettingSignature = null;
            state.clientPortalSetting = new ClientPortalSetting();
            resetRequestingUrl();
        },

        setClientPortalSettingSignature(state, sig) {
            state.clientPortalSettingSignature = sig;
        },

        setClientPortalSetting(state, setting) {
            state.clientPortalSetting = new ClientPortalSetting(setting);
            state.clientPortalSettingSignature = setting.setting_signature;
        },

        setLoggingOut(state, loggingOut) {
            state.loggingOut = loggingOut;
        },

        setAdminMode(state, admin) {
            state.adminMode = admin;
        },

        [NAVIGATION_ORDER_SET] (state, payload) {
            state.user.portal_navigation_order = payload.portal_navigation_order;
        }
    },
    getters: {
        isLogged: state => !!state.user,
        user: state => state.user,
        userInitials(state) {
            return state.user.fname.charAt(0).toUpperCase() + state.user.lname.charAt(0).toUpperCase();
        },
        userNavigationOrder(state) {
            let order = state.user.portal_navigation_order || NAVIGATION_ORDER_DEFAULT;
            return JSON.parse(order);
        },
        showTermsOfService: state => !state.user.terms_accepted && !state.adminMode,
        showNewsletterModal: state => !state.user.seen_latest_newsletter && !state.adminMode,

        getPortfolioCategoryRisks(state) {
            return (portfolioCategory) => {
                if (portfolioCategory == null) {
                    return [];
                }

                let matches = [];

                state.portfolios.forEach((portfolio) => {
                    let regex = new RegExp('(?<=' + portfolioCategory + ' ' + ')\\d', 'g');
                    let m = portfolio.title.match(regex);
                    if (m) {
                        matches.push(parseInt(m[0]));
                    }
                });

                return matches;
            }
        },

        getPortfolioIdByCategoryAndRisk(state) {
            return (category, risk = '') => {
                let portfolio = state.portfolios.find((portfolio) => {
                    let regexStr = risk ? (category + ' ' + risk) : category;
                    let regex = new RegExp(regexStr);
                    return portfolio.title.match(regex) != null
                })

                return portfolio?.id;
            }
        },

        getPortfolioFileByCategoryAndRisk(state) {
            return (category, risk = '') => {
                if (category === 'Fixed Income') {
                    risk = '';
                } else if (risk === null) {
                    // Text that gurantees to match
                    risk = 'xxx';
                }

                let portfolio = state.portfolios.find((portfolio) => {
                    let regexStr = risk ? (category + ' ' + risk) : category;
                    let regex = new RegExp(regexStr);
                    return portfolio.title.match(regex) != null
                })

                return portfolio?.file_link;
            }
        },

        isPageActive(state) {
            return (pageSwitchKey) => {
                if (state.clientPortalSetting[pageSwitchKey] == null) {
                    return true;
                }

                return state.clientPortalSetting[pageSwitchKey];
            }
        },

        defaultPageName(state, getters, rootState, rootGetters) {
            let topPageId = rootGetters['auth/userNavigationOrder'][0];
            let page = rootGetters['draggableNavigation/menus'].find(page => page.id === topPageId);

            if (page && getters.isPageActive(page.pageSwitchKey)) {
                return page.name;
            }

            if (getters.isPageActive('dashboard_page')) {
                return 'Dashboard';
            }

            if (rootGetters['company/isMainCompany']) {
                return 'Portfolios'
            }

            return 'Vault';
        }
    }
};
